/**
 * ElementInspector — V14.62 Sprint 25.
 *
 * Panneau d'édition contextuel qui s'affiche dès qu'un élément est
 * sélectionné dans le HybridEditor. Permet d'éditer en direct :
 *   • Murs    : type (Porteur / Cloison fine / Cloison basse) + épaisseur
 *               (cm si calibré, sinon px) + longueur en lecture seule.
 *   • Escaliers : type (droit / tournant / colimaçon) + dimensions.
 *   • Fenêtres / Portes : largeur, position `t` le long du mur (0..1).
 *
 * Le panneau cherche d'abord dans `walls`, `stairs`, `windows`, `doors`.
 * S'il ne trouve rien (sélection vide ou élément supprimé), il ne rend
 * rien — c'est volontaire pour ne pas voler de l'espace canvas.
 */
import { useMemo } from 'react';
import {
  X, Layers, StretchHorizontal, Square, DoorOpen, Ruler, TriangleRight, Frame,
} from 'lucide-react';
import {
  useGeometryStore, WALL_KIND_DEFS, STAIRS_KINDS, wallKindFor,
} from './useGeometryStore';

const STAIRS_LABELS = [
  { value: STAIRS_KINDS.STRAIGHT, label: 'Droit',     description: '1 volée droite' },
  { value: STAIRS_KINDS.TURNING,  label: 'Tournant',  description: 'Quart / demi-tour' },
  { value: STAIRS_KINDS.SPIRAL,   label: 'Colimaçon', description: 'Hélicoïdal' },
];

const formatMeters = (px, scale) => {
  if (!scale || scale <= 0) return null;
  return (px * scale).toFixed(2);
};

const Row = ({ label, children, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400">
      {label}
    </div>
    {children}
  </div>
);

const NumberField = ({ value, onChange, min, max, step = 1, suffix, testId, disabled }) => (
  <div className="flex items-center gap-1.5">
    <input
      type="number"
      value={Number.isFinite(value) ? value : ''}
      onChange={(e) => {
        const v = e.target.value === '' ? null : Number(e.target.value);
        if (v !== null && !Number.isNaN(v)) onChange(v);
      }}
      min={min} max={max} step={step}
      disabled={disabled}
      data-testid={testId}
      className="flex-1 h-7 px-2 rounded border bg-black/40 border-white/15 text-emerald-100 text-[12px] font-mono focus:outline-none focus:border-emerald-400/50 disabled:opacity-40"
    />
    {suffix && <span className="text-[10px] text-gray-400 font-mono w-6">{suffix}</span>}
  </div>
);

const KindPills = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-1" data-testid={testId}>
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={active}
          data-testid={`${testId}-${opt.value}`}
          title={opt.description}
          className={`inline-flex items-center gap-1 h-7 px-2 rounded text-[10.5px] border transition-colors ${
            active
              ? 'border-emerald-400/70 bg-emerald-500/15 text-emerald-100'
              : 'border-white/10 bg-black/30 text-gray-300 hover:border-emerald-400/30'
          }`}
        >
          {opt.color && (
            <span className="inline-block w-2 h-2 rounded-full"
                  style={{ background: opt.color, boxShadow: active ? `0 0 0 2px ${opt.color}33` : 'none' }} />
          )}
          {opt.label}
        </button>
      );
    })}
  </div>
);

const WallInspector = ({ wall, scale, updateWall }) => {
  const length_px = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
  const length_m = formatMeters(length_px, scale);
  const thickness_cm = scale ? Math.round(wall.thickness * scale * 100) : null;
  // Sprint 30 — mansarde : HSP spécifique du mur (en mètres).
  const hasOverride = Number.isFinite(wall.height_override_m) && wall.height_override_m > 0;
  return (
    <div className="space-y-3" data-testid="inspector-wall">
      <Row label="Type de mur" testId="inspector-wall-kind-row">
        <KindPills
          options={WALL_KIND_DEFS}
          value={wall.kind || 'cloison_fine'}
          onChange={(kind) => updateWall(wall.id, { kind })}
          testId="inspector-wall-kind"
        />
      </Row>
      <Row label={`Épaisseur (${scale ? 'cm' : 'px'})`} testId="inspector-wall-thickness-row">
        {scale ? (
          <NumberField
            value={thickness_cm}
            onChange={(v) => {
              // v is centimeters → convert back to canvas pixels via scale.
              // scale = m/px, so 1 px = scale m = scale*100 cm.
              const newPx = Math.max(1, Math.round(v / (scale * 100)));
              updateWall(wall.id, { thickness: newPx });
            }}
            min={3} max={60} step={1} suffix="cm"
            testId="inspector-wall-thickness-cm"
          />
        ) : (
          <NumberField
            value={wall.thickness}
            onChange={(v) => updateWall(wall.id, { thickness: v })}
            min={2} max={80} step={1} suffix="px"
            testId="inspector-wall-thickness-px"
          />
        )}
      </Row>
      <Row label="Longueur" testId="inspector-wall-length-row">
        <div className="h-7 px-2 rounded border bg-black/20 border-white/10 text-emerald-200 text-[12px] font-mono flex items-center"
             data-testid="inspector-wall-length-value">
          {length_m ? `${length_m} m` : `${length_px.toFixed(0)} px (non calibré)`}
        </div>
      </Row>
      {/* Sprint 30 — Mansarde : HSP spécifique */}
      <Row label="HSP spécifique" testId="inspector-wall-hsp-row">
        <label className="inline-flex items-center gap-2 text-[11.5px] text-gray-300 cursor-pointer"
               data-testid="inspector-wall-hsp-toggle-wrap">
          <input
            type="checkbox"
            checked={hasOverride}
            onChange={(e) => updateWall(wall.id, {
              height_override_m: e.target.checked ? 1.20 : null,
            })}
            className="accent-amber-500 h-3.5 w-3.5"
            data-testid="inspector-wall-hsp-toggle"
          />
          <span>Mansarde / rampant</span>
        </label>
      </Row>
      {hasOverride && (
        <Row label="Hauteur mur bas" testId="inspector-wall-hsp-value-row">
          <NumberField
            value={+wall.height_override_m}
            onChange={(v) => updateWall(wall.id, {
              height_override_m: Math.max(0.5, Math.min(4.0, +v)),
            })}
            min={0.5} max={4.0} step={0.05} suffix="m"
            testId="inspector-wall-hsp-value"
          />
        </Row>
      )}
    </div>
  );
};

const StairsInspector = ({ stairs, scale, updateStairs }) => {
  const w_m = formatMeters(stairs.w, scale);
  const h_m = formatMeters(stairs.h, scale);
  return (
    <div className="space-y-3" data-testid="inspector-stairs">
      <Row label="Type d'escalier" testId="inspector-stairs-kind-row">
        <KindPills
          options={STAIRS_LABELS.map((s) => ({ ...s, color: '#a855f7' }))}
          value={stairs.kind || STAIRS_KINDS.STRAIGHT}
          onChange={(kind) => updateStairs(stairs.id, { kind })}
          testId="inspector-stairs-kind"
        />
      </Row>
      <div className="grid grid-cols-2 gap-2">
        <Row label={`Largeur (${scale ? 'm' : 'px'})`} testId="inspector-stairs-w-row">
          {scale ? (
            <NumberField
              value={Number(w_m)}
              onChange={(v) => updateStairs(stairs.id, { w: Math.round(v / scale) })}
              min={0.5} max={20} step={0.1} suffix="m"
              testId="inspector-stairs-w-m"
            />
          ) : (
            <NumberField
              value={stairs.w}
              onChange={(v) => updateStairs(stairs.id, { w: v })}
              min={8} max={1000} step={1} suffix="px"
              testId="inspector-stairs-w-px"
            />
          )}
        </Row>
        <Row label={`Profondeur (${scale ? 'm' : 'px'})`} testId="inspector-stairs-h-row">
          {scale ? (
            <NumberField
              value={Number(h_m)}
              onChange={(v) => updateStairs(stairs.id, { h: Math.round(v / scale) })}
              min={0.5} max={20} step={0.1} suffix="m"
              testId="inspector-stairs-h-m"
            />
          ) : (
            <NumberField
              value={stairs.h}
              onChange={(v) => updateStairs(stairs.id, { h: v })}
              min={8} max={1000} step={1} suffix="px"
              testId="inspector-stairs-h-px"
            />
          )}
        </Row>
      </div>
    </div>
  );
};

const OpeningInspector = ({ opening, kind, wall, scale, update }) => {
  // Opening width/t are stored in normalized units relative to the wall.
  // `width` is a fraction of 1.0 (we treat it as parametric for clarity).
  const wallLen_px = wall ? Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1) : 0;
  const wallLen_m = formatMeters(wallLen_px, scale);
  const opening_m = wallLen_m ? (Number(wallLen_m) * opening.width).toFixed(2) : null;
  const positionLabel = `${Math.round((opening.t || 0) * 100)} % depuis l'extrémité A`;
  return (
    <div className="space-y-3" data-testid={`inspector-${kind}`}>
      <div className="text-[10px] text-gray-400">
        Sur mur <span className="font-mono text-emerald-300">{wall?.id || '—'}</span>
        {wallLen_m && <> · longueur {wallLen_m} m</>}
      </div>
      <Row label={`Largeur d'ouverture${scale ? ' (m)' : ' (fraction)'}`} testId={`inspector-${kind}-width-row`}>
        {scale && wallLen_m ? (
          <NumberField
            value={Number(opening_m)}
            onChange={(v) => {
              const frac = v / Number(wallLen_m);
              update(opening.id, { width: Math.max(0.03, Math.min(0.6, frac)) });
            }}
            min={0.2} max={4} step={0.05} suffix="m"
            testId={`inspector-${kind}-width-m`}
          />
        ) : (
          <NumberField
            value={Number(opening.width.toFixed(3))}
            onChange={(v) => update(opening.id, { width: v })}
            min={0.05} max={0.6} step={0.01}
            testId={`inspector-${kind}-width-frac`}
          />
        )}
      </Row>
      <Row label={`Position le long du mur · ${positionLabel}`} testId={`inspector-${kind}-position-row`}>
        <input
          type="range"
          min="0.05" max="0.95" step="0.01"
          value={opening.t}
          onChange={(e) => update(opening.id, { t: Number(e.target.value) })}
          data-testid={`inspector-${kind}-position-slider`}
          className="w-full accent-emerald-400"
        />
      </Row>
    </div>
  );
};

const SkewEdgeInspector = ({ edge, scale, updateSkewEdge }) => {
  const length_px = Math.hypot(edge.x2 - edge.x1, edge.y2 - edge.y1);
  const length_m = formatMeters(length_px, scale);
  const low = Number.isFinite(edge.low_side_hsp_m) ? edge.low_side_hsp_m : 1.20;
  return (
    <div className="space-y-3" data-testid="inspector-skew">
      <Row label="Longueur" testId="inspector-skew-length-row">
        <div className="h-7 px-2 rounded border bg-black/20 border-white/10 text-emerald-200 text-[12px] font-mono flex items-center"
             data-testid="inspector-skew-length-value">
          {length_m ? `${length_m} m` : `${length_px.toFixed(0)} px (non calibré)`}
        </div>
      </Row>
      <Row label="Hauteur côté bas" testId="inspector-skew-low-hsp-row">
        <NumberField
          value={+low}
          onChange={(v) => updateSkewEdge(edge.id, { low_side_hsp_m: Math.max(0.5, Math.min(4.0, +v)) })}
          min={0.5} max={4.0} step={0.05} suffix="m"
          testId="inspector-skew-low-hsp"
        />
      </Row>
      <div className="text-[10.5px] text-gray-400 leading-relaxed border-t border-white/5 pt-2">
        <TriangleRight className="w-3 h-3 inline mr-1 text-amber-300" />
        Cette ligne indique où le plafond commence à descendre. La HSP est
        pleine au-dessus, et diminue jusqu&rsquo;à <span className="text-amber-200 font-mono">{low.toFixed(2)} m</span> côté rampant.
      </div>
    </div>
  );
};

const VeluxInspector = ({ velux, updateVelux }) => {
  return (
    <div className="space-y-3" data-testid="inspector-velux">
      <Row label="Position sur brisure (0-1)" testId="inspector-velux-t-row">
        <NumberField
          value={+velux.t}
          onChange={(v) => updateVelux(velux.id, { t: Math.max(0, Math.min(1, +v)) })}
          min={0} max={1} step={0.05} suffix=""
          testId="inspector-velux-t"
        />
      </Row>
      <Row label="Largeur (m)" testId="inspector-velux-width-row">
        <NumberField
          value={+velux.width_m}
          onChange={(v) => updateVelux(velux.id, { width_m: Math.max(0.4, Math.min(2.0, +v)) })}
          min={0.4} max={2.0} step={0.05} suffix="m"
          testId="inspector-velux-width"
        />
      </Row>
      <Row label="Hauteur (m)" testId="inspector-velux-height-row">
        <NumberField
          value={+velux.height_m}
          onChange={(v) => updateVelux(velux.id, { height_m: Math.max(0.6, Math.min(2.5, +v)) })}
          min={0.6} max={2.5} step={0.05} suffix="m"
          testId="inspector-velux-height"
        />
      </Row>
      <div className="text-[10.5px] text-gray-400 border-t border-white/5 pt-2">
        <Frame className="w-3 h-3 inline mr-1 text-sky-400" />
        Fenêtre de toit posée sur le rampant. Formats standard : UK04 (78×118), MK08 (78×140), CK04 (55×98).
      </div>
    </div>
  );
};

const ElementInspector = () => {
  const selectedId  = useGeometryStore((s) => s.selectedId);
  const walls       = useGeometryStore((s) => s.walls);
  const stairs      = useGeometryStore((s) => s.stairs);
  const windows     = useGeometryStore((s) => s.windows);
  const doors       = useGeometryStore((s) => s.doors);
  const skewEdges   = useGeometryStore((s) => s.skew_edges);
  const veluxes     = useGeometryStore((s) => s.velux);
  const scale       = useGeometryStore((s) => s.scale_m_per_px);
  const updateWall  = useGeometryStore((s) => s.updateWall);
  const updateStairs = useGeometryStore((s) => s.updateStairs);
  const updateWindow = useGeometryStore((s) => s.updateWindow);
  const updateDoor   = useGeometryStore((s) => s.updateDoor);
  const updateSkewEdge = useGeometryStore((s) => s.updateSkewEdge);
  const updateVelux    = useGeometryStore((s) => s.updateVelux);
  const clearSelection = useGeometryStore((s) => s.clearSelection);
  const deleteWall     = useGeometryStore((s) => s.deleteWall);
  const deleteStairs   = useGeometryStore((s) => s.deleteStairs);
  const deleteWindow   = useGeometryStore((s) => s.deleteWindow);
  const deleteDoor     = useGeometryStore((s) => s.deleteDoor);
  const deleteSkewEdge = useGeometryStore((s) => s.deleteSkewEdge);
  const deleteVelux    = useGeometryStore((s) => s.deleteVelux);

  const found = useMemo(() => {
    if (!selectedId) return null;
    const wall = walls.find((w) => w.id === selectedId);
    if (wall) return { kind: 'wall', wall };
    const stair = stairs.find((s) => s.id === selectedId);
    if (stair) return { kind: 'stairs', stairs: stair };
    const win = windows.find((w) => w.id === selectedId);
    if (win) return { kind: 'window', opening: win, wall: walls.find((w) => w.id === win.wallId) };
    const door = doors.find((d) => d.id === selectedId);
    if (door) return { kind: 'door',   opening: door, wall: walls.find((w) => w.id === door.wallId) };
    const skew = skewEdges.find((e) => e.id === selectedId);
    if (skew) return { kind: 'skew',   skew };
    const vx = veluxes.find((v) => v.id === selectedId);
    if (vx) return { kind: 'velux',  velux: vx };
    return null;
  }, [selectedId, walls, stairs, windows, doors, skewEdges, veluxes]);

  if (!found) return null;

  const headerByKind = {
    wall:   { icon: Layers,             label: 'Mur',                color: wallKindFor(found.wall?.kind).color },
    stairs: { icon: StretchHorizontal,  label: 'Escalier',           color: '#a855f7' },
    window: { icon: Square,             label: 'Fenêtre',            color: '#3b82f6' },
    door:   { icon: DoorOpen,           label: 'Porte',              color: '#f59e0b' },
    skew:   { icon: TriangleRight,      label: 'Ligne de brisure',   color: '#fb923c' },
    velux:  { icon: Frame,              label: 'Velux',              color: '#0ea5e9' },
  }[found.kind];

  const Icon = headerByKind.icon;

  const onDelete = () => {
    if (found.kind === 'wall')   deleteWall(found.wall.id);
    if (found.kind === 'stairs') deleteStairs(found.stairs.id);
    if (found.kind === 'window') deleteWindow(found.opening.id);
    if (found.kind === 'door')   deleteDoor(found.opening.id);
    if (found.kind === 'skew')   deleteSkewEdge(found.skew.id);
    if (found.kind === 'velux')  deleteVelux(found.velux.id);
  };

  return (
    <aside
      className="absolute right-2 top-2 w-[280px] rounded-lg border overflow-hidden shadow-xl backdrop-blur z-20"
      style={{ background: 'rgba(2,6,12,0.92)', borderColor: 'rgba(16,185,129,0.35)' }}
      data-testid="element-inspector"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b"
           style={{ borderColor: 'rgba(16,185,129,0.25)' }}>
        <Icon className="w-3.5 h-3.5" style={{ color: headerByKind.color }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80 leading-none">
            {headerByKind.label}
          </div>
          <div className="text-[10px] text-gray-400 font-mono truncate mt-0.5"
               data-testid="inspector-id">
            {found.wall?.id || found.stairs?.id || found.opening?.id}
          </div>
        </div>
        {!scale && (
          <span className="inline-flex items-center gap-1 text-[9.5px] text-amber-300/90 font-mono"
                title="Calibrez avec l'outil Règle (C) pour saisir les dimensions en m">
            <Ruler className="w-3 h-3" /> non calibré
          </span>
        )}
        <button type="button"
                onClick={() => clearSelection()}
                title="Fermer l'inspecteur (Échap)"
                data-testid="inspector-close-btn"
                className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-white/5 text-gray-400 hover:text-gray-200">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Body */}
      <div className="p-3">
        {found.kind === 'wall'   && <WallInspector   wall={found.wall} scale={scale} updateWall={updateWall} />}
        {found.kind === 'stairs' && <StairsInspector stairs={found.stairs} scale={scale} updateStairs={updateStairs} />}
        {found.kind === 'window' && <OpeningInspector opening={found.opening} kind="window" wall={found.wall} scale={scale} update={updateWindow} />}
        {found.kind === 'door'   && <OpeningInspector opening={found.opening} kind="door"   wall={found.wall} scale={scale} update={updateDoor} />}
        {found.kind === 'skew'   && <SkewEdgeInspector edge={found.skew} scale={scale} updateSkewEdge={updateSkewEdge} />}
        {found.kind === 'velux'  && <VeluxInspector velux={found.velux} updateVelux={updateVelux} />}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t flex items-center justify-between"
           style={{ borderColor: 'rgba(16,185,129,0.18)', background: 'rgba(2,6,12,0.6)' }}>
        <span className="text-[9.5px] text-gray-500">
          Échap pour fermer
        </span>
        <button type="button"
                onClick={onDelete}
                data-testid="inspector-delete-btn"
                className="h-7 px-3 rounded text-[10.5px] font-mono border border-rose-400/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20">
          Supprimer
        </button>
      </div>
    </aside>
  );
};

export default ElementInspector;
